import { useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { UseFilterBuilderReturn } from "../hooks/useFilterBuilder";
import { useIsDeskUser } from "@/hooks/useIsDeskUser";
import useAuth from "@/hooks/useAuth";
import DeskPublishFields from "./DeskPublishFields";
import PublishConfirmDialog from "./PublishConfirmDialog";
import { formatListName } from "@/shared/utils/formatListName";
import { savedViewColor } from "@/shared/utils/savedViewHighlight";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { Portfolio } from "@/types/Portfolio";
import { boxesFor, isBroadTag, swampingHint, type Box } from "../builderModel";
import { suggestTickers } from "../tickerSuggestions";
import { contentOf } from "../includedLists";
import MatchBox from "./builder/MatchBox";
import RuleSpine from "./builder/RuleSpine";
import OperatorSwitch from "./builder/OperatorSwitch";
import ChipInput from "./builder/ChipInput";
import TermChip, { CheckPanel, FlagChip } from "./builder/TermChip";
import { KeywordTagSuggestions, TagBrowse, TagSearchField, type PickedTag } from "./builder/TagSearch";
import { useKeywordTags } from "./builder/useKeywordTags";
import CsvImportStep from "./builder/CsvImportStep";
import WhenMatchesSection from "./builder/WhenMatchesSection";

interface QueryBuilderPaneProps {
  builder: UseFilterBuilderReturn;
  onCancel?: () => void;
}

/** Typed tickers shown before "+N more": a client book can hold hundreds. */
const TICKER_CAP = 16;

const MENU_TAG_IDS = new Set(
  menuCategories.flatMap((group) => group.items.map((item) => String(item.id)))
);

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** What a list chip holds, for its hover text. */
function holds(tickers: number, keywords: number, tags: number): string {
  const parts = [
    tickers > 0 ? plural(tickers, "ticker") : "",
    keywords > 0 ? plural(keywords, "keyword") : "",
    tags > 0 ? plural(tags, "tag") : "",
  ].filter(Boolean);
  return parts.length === 0 ? "Nothing in it yet" : parts.join(", ");
}

/**
 * The builder: a name, then the rule as a sentence — "Matches headlines
 * that mention any of these keywords, or are about any of these tickers, or
 * are tagged with any of these" — then what happens on a match.
 *
 * Each box takes its own entries, typed or pasted straight in, and shows the
 * watchlists picked on the left by what they hold. The or | and switches
 * between the boxes are the server's own operators (see builderModel.ts), so
 * the sentence on screen is the rule that runs.
 */
export default function QueryBuilderPane({ builder, onCancel }: QueryBuilderPaneProps) {
  const isDeskUser = useIsDeskUser();
  const { authenticated } = useAuth();
  const { terms } = builder;

  /** A tag watchlist edited in place: its tags are all it holds. */
  const tagsOnly = builder.inPlace && builder.editingKind === "tag";
  /** A Newsquawk watchlist edited in place: terms only, matched as "any". */
  const termsOnly = builder.inPlace && builder.editingKind === "watchlist";
  /**
   * Publishing to the Newsquawk library.
   *
   * Not on an edit of the desk member's own list — the server ignores
   * owner_type on PATCH, so the tick would do nothing.
   */
  const showDeskFields = isDeskUser && (!builder.isEditing || builder.isNewsquawkOwned);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  const [keywordDraft, setKeywordDraft] = useState("");
  const [tickerDraft, setTickerDraft] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);
  /** Which check is open: undefined means the first one; null, none. */
  const [openCheck, setOpenCheck] = useState<string | null | undefined>(undefined);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [showAllTickers, setShowAllTickers] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  /** The keyword expression being written, when open. */
  const [expressionDraft, setExpressionDraft] = useState<string | null>(null);

  /** Publishing is irreversible and live on save — ask on the way in only. */
  const handleSave = () => {
    if (isDeskUser && builder.publishToLibrary && !builder.isNewsquawkOwned) {
      setConfirmingPublish(true);
      return;
    }
    builder.handleSubmit();
  };

  // ── What each box shows ──────────────────────────────────────────────
  const listChip = (p: Portfolio) => ({
    key: `p-${p.id}`,
    label: formatListName(p.name),
    dot: p.color,
    title: holds(p.tickers.length, p.keywords?.length ?? 0, 0),
    onRemove: () => builder.togglePortfolio(p.id),
  });
  const unitChip = (q: CategoryQuery) => {
    const portfolios = q.portfolios ?? [];
    return {
      key: `c-${q.id}`,
      label: formatListName(q.queryName),
      dot: savedViewColor(q),
      title: holds(
        new Set(portfolios.flatMap((p) => p.tickers)).size,
        new Set(portfolios.flatMap((p) => p.keywords ?? [])).size +
          (q.searchKeyword?.trim() ? 1 : 0),
        (q.categories?.length ?? 0) + (q.tagsWatchlistIds?.length ?? 0)
      ),
      onRemove: () => q.id !== undefined && builder.toggleIncluded(q.id),
    };
  };
  const unitsIn = (box: Box, units: CategoryQuery[]) =>
    units.filter((q) => boxesFor(contentOf(q)).includes(box));

  const mergeable = builder.includedLists.filter((q) => !builder.blockedIncluded.includes(q));
  const { tickerOnly, keywordOnly, mixed } = builder.selectedGrouped;
  const keywordLists = [...keywordOnly, ...mixed].map(listChip);
  const tickerLists = [...tickerOnly, ...mixed].map(listChip);
  const keywordUnits = unitsIn("keywords", mergeable).map(unitChip);
  const tickerUnits = unitsIn("tickers", mergeable).map(unitChip);
  const tagUnits = unitsIn("tags", mergeable).map(unitChip);
  const tagWatchlists = builder.selectedTagWatchlistObjects.map((tw) => ({
    key: `t-${tw.id}`,
    label: formatListName(tw.name),
    dot: null,
    title: plural(tw.tags.length, "tag"),
    onRemove: () => builder.toggleTagWatchlist(tw.id),
  }));
  /** AND lists, waiting to be started from or removed, in their first box. */
  const blockedIn = (box: Box) =>
    builder.blockedIncluded.filter((q) => boxesFor(contentOf(q))[0] === box);

  const selectedTagIds = useMemo(
    () => new Set(builder.selectedCategories.map((c) => String(c.id))),
    [builder.selectedCategories]
  );
  const keywordTags = useKeywordTags(terms.keywords);

  // ── Checks ───────────────────────────────────────────────────────────
  const checkKeys = [
    ...terms.checks.map((c) => `w-${c.word}`),
    ...builder.blockedIncluded.map((q) => `c-${q.id}`),
  ];
  const activeCheck =
    openCheck === undefined ? checkKeys[0] : openCheck && checkKeys.includes(openCheck) ? openCheck : undefined;
  const toggleCheck = (key: string) => setOpenCheck(activeCheck === key ? null : key);

  const findInTags = (word: string) => {
    terms.resolveCheck(word, "remove");
    setTagQuery(word);
    setOpenCheck(undefined);
    requestAnimationFrame(() => tagInputRef.current?.focus());
  };

  const checkPanelFor = (box: Box) => {
    if (!activeCheck) return null;
    if (activeCheck.startsWith("w-") && box === "tickers") {
      const check = terms.checks.find((c) => `w-${c.word}` === activeCheck);
      if (!check) return null;
      const settle = (as: "ticker" | "keyword" | "remove") => {
        terms.resolveCheck(check.word, as);
        setOpenCheck(undefined);
      };
      return check.reason === "unknown" ? (
        <CheckPanel
          message={`Is “${check.word}” a ticker?`}
          actions={[
            { label: "Keep it as a ticker", onClick: () => settle("ticker"), primary: true },
            { label: "Make it a keyword", onClick: () => settle("keyword") },
            { label: "Remove", onClick: () => settle("remove") },
          ]}
        />
      ) : (
        <CheckPanel
          message={`“${check.word}” doesn’t look like a ticker.`}
          actions={[
            ...(termsOnly
              ? []
              : [{ label: "Find it in tags", onClick: () => findInTags(check.word), primary: true }]),
            { label: "Make it a keyword", onClick: () => settle("keyword"), primary: termsOnly },
            { label: "Remove", onClick: () => settle("remove") },
          ]}
        />
      );
    }
    const unit = builder.blockedIncluded.find(
      (q) => `c-${q.id}` === activeCheck && boxesFor(contentOf(q))[0] === box
    );
    if (!unit) return null;
    const name = formatListName(unit.queryName);
    return (
      <CheckPanel
        message={`“${name}” uses AND, so it can’t be added to another watchlist yet.`}
        actions={[
          { label: "Start from it", onClick: () => builder.startFrom(unit), primary: true },
          {
            label: "Remove",
            onClick: () => unit.id !== undefined && builder.toggleIncluded(unit.id),
          },
        ]}
      />
    );
  };

  const blockedChips = (box: Box) =>
    blockedIn(box).map((q) => (
      <FlagChip
        key={`c-${q.id}`}
        word={formatListName(q.queryName)}
        open={activeCheck === `c-${q.id}`}
        onClick={() => toggleCheck(`c-${q.id}`)}
      />
    ));

  // ── Keywords ─────────────────────────────────────────────────────────
  const expression = builder.searchKeyword.trim();
  const keywordEntries = terms.keywords.length + keywordLists.length + keywordUnits.length;
  /**
   * An older list's "each keyword list must match", kept as loaded but not
   * offered — see keywordWatchlistOp. Said, so the box doesn't claim "any".
   */
  const keywordGroups =
    keywordOnly.length + (expression ? 1 : 0) + (terms.keywords.length > 0 ? 1 : 0);
  const eachKeywordList = builder.keywordWatchlistOp === "AND" && keywordGroups >= 2;

  const keywordsBox = !tagsOnly && (
    <MatchBox
      label="Keywords"
      title={eachKeywordList ? "mention something from each of these" : "mention any of these keywords"}
      action={
        eachKeywordList ? (
          <button
            type="button"
            onClick={() => builder.setKeywordWatchlistOp("OR")}
            className="rounded border-0 bg-transparent p-0 text-xs font-normal text-squawk-blue hover:text-squawk-blue/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
          >
            Match any instead
          </button>
        ) : undefined
      }
      field={
        <>
          {terms.keywords.map((k) => (
            <TermChip key={`k-${k}`} kind="keyword" label={k} onRemove={() => terms.removeKeyword(k)} />
          ))}
          {[...keywordLists, ...keywordUnits].map(({ key, ...chip }) => (
            <TermChip key={key} kind="list" {...chip} />
          ))}
          {blockedChips("keywords")}
          <ChipInput
            value={keywordDraft}
            onChange={setKeywordDraft}
            onCommit={(text) => {
              terms.addKeywords(text);
              setKeywordDraft("");
            }}
            placeholder={keywordEntries > 0 ? "Add keywords" : "Add keywords, e.g. tariffs, rate cut"}
            ariaLabel="Add keywords"
          />
        </>
      }
    >
      {checkPanelFor("keywords")}
      {!termsOnly && expression && expressionDraft === null && (
        <div className="flex items-center gap-2 rounded border border-purple-400/30 bg-purple-900/20 py-1 pl-2.5 pr-1">
          <span className="shrink-0 text-xs text-gray-400">Together</span>
          <span className="min-w-0 flex-1 truncate text-[13px] text-purple-200" title={expression}>
            {expression}
          </span>
          <button
            type="button"
            onClick={() => setExpressionDraft(builder.searchKeyword)}
            className="h-[26px] rounded border border-gray-600 bg-gray-800 px-2.5 py-0 text-xs font-medium text-gray-200 hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => builder.setSearchKeyword("")}
            aria-label="Remove the keywords that must appear together"
            className="flex size-[26px] items-center justify-center rounded border-0 bg-transparent p-0 text-purple-300 hover:bg-purple-900/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
          >
            <XMarkIcon aria-hidden="true" strokeWidth={2} className="size-3" />
          </button>
        </div>
      )}
      {!termsOnly && expressionDraft !== null && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="wl-expression" className="text-[13px] text-gray-200">
            Keywords that must appear together
          </label>
          <textarea
            id="wl-expression"
            rows={2}
            value={expressionDraft}
            onChange={(e) => setExpressionDraft(e.target.value)}
            placeholder='trump AND powell OR "rate cut"'
            className="w-full resize-y rounded-md border border-gray-700 bg-gray-800 px-2.5 py-2 text-[13px] text-gray-100 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none focus:ring-1 focus:ring-squawk-blue"
          />
          <p className="m-0 text-xs text-gray-400">
            AND needs both, OR needs either. Put a phrase in quotes.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                builder.setSearchKeyword(expressionDraft.trim());
                setExpressionDraft(null);
              }}
              className="h-[30px] rounded-md border-0 bg-squawk-blue px-3.5 py-0 text-[13px] font-semibold text-gray-950 hover:bg-squawk-blue/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
            >
              Done
            </button>
            <button
              type="button"
              onClick={() => setExpressionDraft(null)}
              className="h-[30px] rounded-md border border-gray-600 bg-gray-800 px-3 py-0 text-[13px] font-medium text-gray-200 hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/60"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {!termsOnly && !expression && expressionDraft === null && keywordEntries >= 2 && (
        <div className="flex">
          <button
            type="button"
            onClick={() => setExpressionDraft("")}
            className="rounded border-0 bg-transparent p-0 text-xs font-normal text-squawk-blue hover:text-squawk-blue/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
          >
            Need two words together? Combine with AND
          </button>
        </div>
      )}
    </MatchBox>
  );

  // ── Tickers ──────────────────────────────────────────────────────────
  const shownTickers = showAllTickers ? terms.tickers : terms.tickers.slice(0, TICKER_CAP);
  const tickerEntries = terms.tickers.length + tickerLists.length + tickerUnits.length;
  const tickerSuggestions = suggestTickers(builder.tickerIndex, tickerDraft, {
    exclude: terms.tickers,
  }).map((symbol) => ({ key: symbol, label: symbol }));

  const tickersBox = !tagsOnly && (
    <MatchBox
      label="Tickers"
      title="are about any of these tickers"
      action={
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded text-xs text-squawk-blue hover:text-squawk-blue/80 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-squawk-blue/70">
          <input
            type="file"
            accept=".csv,text/csv,.txt"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setCsvFile(file);
              e.target.value = "";
            }}
          />
          <ArrowUpTrayIcon aria-hidden="true" className="size-3.5" />
          Upload CSV
        </label>
      }
      field={
        <>
          {shownTickers.map((t) => (
            <TermChip key={`t-${t}`} kind="ticker" label={t} onRemove={() => terms.removeTicker(t)} />
          ))}
          {[...tickerLists, ...tickerUnits].map(({ key, ...chip }) => (
            <TermChip key={key} kind="list" {...chip} />
          ))}
          {terms.checks.map((check) => (
            <FlagChip
              key={`w-${check.word}`}
              word={check.word}
              open={activeCheck === `w-${check.word}`}
              onClick={() => toggleCheck(`w-${check.word}`)}
            />
          ))}
          {blockedChips("tickers")}
          {terms.tickers.length > TICKER_CAP && (
            <button
              type="button"
              onClick={() => setShowAllTickers((v) => !v)}
              className="h-[26px] rounded border-0 bg-transparent px-1.5 py-0 text-[13px] font-normal text-squawk-blue hover:text-squawk-blue/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
            >
              {showAllTickers ? "Show fewer" : `+${(terms.tickers.length - TICKER_CAP).toLocaleString()} more`}
            </button>
          )}
          <ChipInput
            value={tickerDraft}
            onChange={setTickerDraft}
            suggestions={tickerSuggestions}
            onCommit={(text) => {
              terms.addTickers(text, builder.isKnownTicker);
              setTickerDraft("");
              setOpenCheck(undefined);
            }}
            onPick={(s) => {
              terms.addSymbols([s.label]);
              setTickerDraft("");
            }}
            placeholder={
              tickerEntries > 0 || terms.checks.length > 0
                ? "Add ticker symbols"
                : "Add ticker symbols, e.g. AAPL, BRK.B"
            }
            ariaLabel="Add ticker symbols"
          />
        </>
      }
    >
      {checkPanelFor("tickers")}
      {csvFile && (
        <CsvImportStep
          file={csvFile}
          onAdd={(symbols) => {
            terms.addSymbols(symbols);
            setCsvFile(null);
          }}
          onCancel={() => setCsvFile(null)}
        />
      )}
    </MatchBox>
  );

  // ── Tags ─────────────────────────────────────────────────────────────
  const tagEntries =
    builder.selectedCategories.length + tagWatchlists.length + tagUnits.length;
  const addTag = (tag: PickedTag) => {
    if (tag.category) builder.rememberTagKinds({ [String(tag.id)]: tag.category });
    if (selectedTagIds.has(String(tag.id))) return;
    builder.toggleCategory({ title: tag.name, id: tag.id });
  };
  const toggleTag = (tag: PickedTag) => builder.toggleCategory({ title: tag.name, id: tag.id });

  const tagsBox = !termsOnly && (
    <MatchBox
      label="Tags"
      title="are tagged with any of these"
      field={
        <>
          {builder.selectedCategories.map((c) => (
            <TermChip
              key={`g-${c.id}`}
              kind="tag"
              label={c.title}
              onRemove={() => builder.toggleCategory(c)}
            />
          ))}
          {[...tagWatchlists, ...tagUnits].map(({ key, ...chip }) => (
            <TermChip key={key} kind="list" {...chip} />
          ))}
          {blockedChips("tags")}
          <TagSearchField
            ref={tagInputRef}
            query={tagQuery}
            onQueryChange={setTagQuery}
            selectedIds={selectedTagIds}
            onAdd={addTag}
            hasChips={tagEntries > 0}
          />
          <button
            type="button"
            onClick={() => setBrowseOpen((v) => !v)}
            aria-expanded={browseOpen}
            className="h-[26px] rounded border-0 bg-transparent px-1 py-0 text-xs font-normal text-squawk-blue hover:text-squawk-blue/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
          >
            {browseOpen ? "Hide tags" : "Browse"}
          </button>
        </>
      }
    >
      {checkPanelFor("tags")}
      {!tagsOnly && (
        <KeywordTagSuggestions tags={keywordTags} selectedIds={selectedTagIds} onAdd={addTag} />
      )}
      {browseOpen && <TagBrowse selectedIds={selectedTagIds} onToggle={toggleTag} />}
    </MatchBox>
  );

  // ── The rule ─────────────────────────────────────────────────────────
  const { shape } = builder;
  const hint = swampingHint({
    frame: shape.frame,
    typedTickers: terms.tickers.length,
    tickerLists: [...tickerLists, ...tickerUnits].map((chip) => chip.label),
    tags: [
      ...builder.selectedCategories.map((c) => ({
        name: c.title,
        broad: MENU_TAG_IDS.has(String(c.id)) || isBroadTag(builder.tagKinds[String(c.id)]),
      })),
      ...builder.selectedTagWatchlistObjects.map((tw) => ({
        name: formatListName(tw.name),
        broad: tw.tags.some((id) => MENU_TAG_IDS.has(id) || isBroadTag(builder.tagKinds[id])),
      })),
      ...unitsIn("tags", mergeable).map((q) => ({
        name: formatListName(q.queryName),
        broad: (q.categories ?? []).some(
          (id) => MENU_TAG_IDS.has(String(id)) || isBroadTag(builder.tagKinds[String(id)])
        ),
      })),
    ],
  });

  const tooMany = builder.keywordsSize.count > 0 && !builder.keywordsSize.ok;

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        <div className="flex max-w-[740px] flex-col gap-[22px] px-7 pb-9 pt-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="wl-name" className="sr-only">
              Watchlist name
            </label>
            <input
              id="wl-name"
              type="text"
              value={builder.queryName}
              onChange={(e) => builder.setQueryName(e.target.value)}
              placeholder="Name this watchlist"
              className="h-[42px] w-full rounded-md border border-gray-700 bg-gray-800 px-3 text-[15px] font-medium text-gray-100 placeholder:font-normal placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none focus:ring-1 focus:ring-squawk-blue"
            />
            {/* Logged out, saving writes nothing — say so before the button
                does. The guided demo arrives with a list the concierge
                assembled; "Build it myself" arrives empty. */}
            {!authenticated && (
              <p className="m-0 text-xs text-gray-400">
                {builder.fromDraft
                  ? "This is what the builder made from your description. Nothing is saved in the demo."
                  : "Nothing is saved in the demo."}
              </p>
            )}
            {builder.inPlace && (
              <p className="m-0 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-200">
                {builder.inPlaceReason === "ingredient"
                  ? "Another of your watchlists includes this one, so it stays a list of tags, and changes here reach that watchlist too. To combine it with terms or other lists, make a new watchlist that includes it."
                  : tagsOnly
                    ? "Customers use this Newsquawk list as it is, so it stays a list of tags: change its tags here. To combine it with terms or other lists, make a new Newsquawk watchlist that includes it."
                    : "Customers use this Newsquawk watchlist as it is, so it stays a watchlist: change its terms here. Tags and other lists can't be added. To combine it, make a new Newsquawk watchlist that includes it."}
              </p>
            )}
            {builder.formErrors.categories && (
              <p role="alert" className="m-0 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                {builder.formErrors.categories}
              </p>
            )}
            {(builder.formErrors.keywords || tooMany) && (
              <p role="alert" className="m-0 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                {builder.formErrors.keywords ||
                  builder.keywordsSize.error ||
                  `${builder.keywordsSize.count}/${builder.MAX_KEYWORDS_COUNT} keywords`}
              </p>
            )}
          </div>

          {/* Straight under the name, where the other builders put it. */}
          {showDeskFields && (
            <DeskPublishFields
              publish={builder.publishToLibrary}
              onPublishChange={builder.setPublishToLibrary}
              description={builder.description}
              onDescriptionChange={builder.setDescription}
              whenToUse={builder.whenToUse}
              onWhenToUseChange={builder.setWhenToUse}
              categories={builder.assetClasses}
              onCategoriesChange={builder.setAssetClasses}
              alreadyPublished={builder.isNewsquawkOwned}
            />
          )}

          <section aria-labelledby="wl-rule" className="flex flex-col gap-3">
            <h2 id="wl-rule" className="m-0 text-sm font-semibold text-gray-100">
              Matches headlines that…
            </h2>
            <RuleSpine
              keywords={keywordsBox || undefined}
              tickers={tickersBox || undefined}
              tags={tagsBox || undefined}
              keywordSwitch={
                <OperatorSwitch
                  label="How keywords combine with tickers and tags"
                  state={termsOnly ? { ...shape.keywordOp, shown: false, heldBy: undefined } : shape.keywordOp}
                  value={builder.keywordOp}
                  onChange={builder.setKeywordOp}
                />
              }
              filterSwitch={
                <OperatorSwitch
                  label="How tickers combine with tags"
                  state={shape.filterMode}
                  value={builder.filterMode}
                  onChange={builder.setFilterMode}
                />
              }
              frame={shape.frame}
              hint={hint}
            />
            {shape.usesAnd && !termsOnly && (
              <p className="m-0 text-xs text-gray-400">
                A watchlist that uses AND filters a panel on its own.
              </p>
            )}
          </section>

          <WhenMatchesSection
            color={builder.displayColor}
            onColorChange={builder.setColor}
            notifications={builder.notifications}
            onNotificationsChange={builder.setNotifications}
            onResetColor={builder.hasLibraryOverride ? builder.resetLibraryLook : undefined}
          />
        </div>
      </div>

      <Footer builder={builder} onCancel={onCancel} onSave={handleSave} />

      <PublishConfirmDialog
        open={confirmingPublish}
        listName={builder.queryName.trim() || "Untitled watchlist"}
        onCancel={() => setConfirmingPublish(false)}
        onConfirm={() => {
          setConfirmingPublish(false);
          builder.handleSubmit();
        }}
      />
    </div>
  );
}

function Footer({
  builder,
  onCancel,
  onSave,
}: {
  builder: UseFilterBuilderReturn;
  onCancel?: () => void;
  onSave: () => void;
}) {
  const disabled =
    builder.isSubmitting || !builder.queryName.trim() || builder.copyUnchanged || builder.toCheck > 0;
  return (
    <div
      className={clsx(
        "flex shrink-0 flex-col gap-2 border-t border-gray-800 px-5 py-3 sm:flex-row sm:items-center",
        builder.isEditing ? "sm:justify-between" : "sm:justify-end"
      )}
    >
      {builder.isEditing && (
        <button
          type="button"
          onClick={builder.handleDelete}
          disabled={builder.isDeleting}
          className="order-3 h-9 w-full rounded-md border border-red-800/50 bg-transparent px-4 py-0 text-sm font-medium text-red-400 transition-colors hover:bg-red-900/20 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 disabled:opacity-50 sm:order-1 sm:w-auto"
        >
          {builder.isDeleting ? "Deleting..." : "Delete"}
        </button>
      )}
      {/* What the save will do, said before it happens. A copy of a
          Newsquawk list saves nothing until something changes, and what
          changed decides whether it is kept as the user's look for the
          library list or saved as a list of their own. */}
      {builder.basedOn && (
        <p className="order-2 m-0 text-xs text-gray-400 sm:order-1 sm:mr-auto">
          {builder.copyUnchanged
            ? builder.libraryLookable
              ? builder.libraryKind === "watchlist"
                ? "Change its colour to keep it for you, or its name, terms or alerts to save your own version."
                : "Change its colour to keep it for you, or its name, lists, tags or alerts to save your own version."
              : `Change something to save your own version. To use “${builder.basedOn}” as it is, pick it from any widget's filter or highlight menu.`
            : builder.lookOnly
              ? `Only for you — “${builder.basedOn}” itself doesn't change.`
              : builder.libraryContentChanged
                ? builder.libraryKind === "watchlist"
                  ? "You've changed its name, terms or alerts, so this saves a new watchlist of your own."
                  : "You've changed its name, lists, tags or alerts, so this saves a new watchlist of your own."
                : null}
        </p>
      )}
      <div className="order-1 flex flex-col gap-2 sm:order-2 sm:flex-row">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="h-9 w-full rounded-md border border-gray-600 bg-gray-800 px-4 py-0 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-700 hover:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/60 sm:w-auto"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={disabled}
          className="h-9 w-full rounded-md border-0 bg-squawk-blue px-[18px] py-0 text-sm font-semibold text-gray-950 transition-colors hover:bg-squawk-blue/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {builder.isSubmitting
            ? "Saving..."
            : builder.toCheck > 0
              ? `Check ${plural(builder.toCheck, "entry", "entries")} first`
              : builder.isEditing
                ? "Save changes"
                : builder.basedOn
                  ? builder.libraryLookable && !builder.libraryContentChanged
                    ? "Save colour"
                    : "Save as new watchlist"
                  : "Save watchlist"}
        </button>
      </div>
    </div>
  );
}
